import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Alert, Button, Form, Row, Col } from 'react-bootstrap';
import { BiCloud } from 'react-icons/bi';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import AppContext from 'Utils/AppContext';
import { listStaging, createStaging, updateStaging, deleteStaging, testStaging, listStagingRuns, deleteRun, importStagingSubsets } from '../tdmApi';
import { fmtWhen, fmtNum, fmtBytes } from '../tdmUtils';
import StagingCredentialFields from './StagingCredentialFields';
import { useConfirm } from 'Utils/ConfirmDialog';
import Breadcrumbs from 'Utils/Breadcrumbs';

const TYPES = [['S3', 'Amazon S3'], ['AZURE_BLOB', 'Azure Blob Storage'], ['SFTP', 'SFTP']];
const ROOT_HINT = { S3: 's3://bucket/prefix', AZURE_BLOB: 'azblob://account/container/prefix', SFTP: '/absolute/path/on/server' };
const TERMINAL = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

const REASON_LABEL = { SKIPPED_EXISTS: 'already on this platform', DELETED: 'deleted by its platform', UNREADABLE: 'unreadable subset.json' };

// "3 subsets found · 2 imported · 1 refreshed · 0 skipped" plus one line per skipped / failed folder.
const ImportSummary = ({ result }) => {
  const imported = result.imported || [];
  const created = imported.filter((i) => i.created);
  const refreshed = imported.filter((i) => !i.created);
  const skipped = result.skipped || [];
  const failed = result.failed || [];
  const runsSkipped = imported.flatMap((i) => (i.runsSkipped || []).map((r) => `${i.slug}: ${r}`));
  return (
    <span>
      {`${result.found} subset${result.found === 1 ? '' : 's'} found in storage · ${created.length} imported · ${refreshed.length} refreshed · ${skipped.length} skipped · ${failed.length} failed`}
      {created.length > 0 && <div className='tdm-note'>Imported: {created.map((i) => i.name).join(', ')} — read-only, not attached to data sources; runs with a clean mask can be deployed.</div>}
      {skipped.map((s) => <div className='tdm-note' key={`s-${s.subsetPath}`}>{s.subsetPath}: {REASON_LABEL[s.reason] || s.reason}{s.detail ? ` — ${s.detail}` : ''}</div>)}
      {failed.map((f) => <div className='tdm-note tdm-error' key={`f-${f.subsetPath}`}>{f.subsetPath}: {f.error}</div>)}
      {runsSkipped.map((r) => <div className='tdm-note' key={`r-${r}`}>run skipped — {r}</div>)}
    </span>
  );
};

const blank = () => ({ name: '', type: 'S3', rootPath: '', credentialMode: 'INLINE', credentials: {} });

const fromResponse = (st) => ({
  name: st.name, type: st.type, rootPath: st.rootPath, credentialMode: st.credentialMode || 'INLINE',
  credentials: { ...(st.settings || {}) },
});

// Staging areas: where subset runs are stored. Left: the list; right: the editor for the selected
// one plus the runs kept there. Credential writes are admin-only on the platform.
const StagingManager = () => {
  const { confirm, confirmDialog } = useConfirm();
  const history = useHistory();
  const { isAdmin } = useContext(AppContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);   // null = new
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [runs, setRuns] = useState([]);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await listStaging();
      setItems(Array.isArray(list) ? list : []);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Could not load staging areas');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = items.find((s) => String(s.id) === String(selectedId)) || null;
  useEffect(() => {
    setTestResult(null);
    setNotice(null);
    if (selected) {
      setForm(fromResponse(selected));
      listStagingRuns(selected.id).then((r) => setRuns(Array.isArray(r) ? r : [])).catch(() => setRuns([]));
    } else {
      setForm(blank());
      setRuns([]);
    }
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updateCred = (k, v) => setForm((f) => ({ ...f, credentials: { ...f.credentials, [k]: v } }));

  const save = async (e) => {
    if (e) e.preventDefault();
    setSaving(true);
    setNotice(null);
    try {
      const body = {
        name: form.name, type: form.type, rootPath: form.rootPath, credentialMode: form.credentialMode,
        credentials: form.credentials,
      };
      // A root that is new to this platform may already hold subsets (another install's, or a
      // restored one's): read them in right away. A credential-only edit changes nothing in staging.
      const rootChanged = !selected || selected.rootPath !== form.rootPath || selected.type !== form.type;
      const saved = selected ? await updateStaging(selected.id, body) : await createStaging(body);
      await load();
      setSelectedId(saved.id);
      setNotice({ tone: 'success', text: `Saved ${saved.name}.` });
      if (rootChanged) await runImport(saved.id);
    } catch (err) {
      setNotice({ tone: 'danger', text: err?.response?.data?.message || err?.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    if (!(await confirm({
      title: `Delete staging "${selected.name}"?`,
      body: "This removes the platform's pointer to the storage. Files already in the bucket are NOT touched. Delete the runs below first if you want the data gone.",
      confirmLabel: 'Delete staging',
      variant: 'danger',
    }))) return;
    try {
      await deleteStaging(selected.id);
      setSelectedId(null);
      await load();
    } catch (err) {
      setNotice({ tone: 'danger', text: err?.response?.data?.message || err?.message || 'Delete failed' });
    }
  };

  // Deletes the run row AND purges its folder from the bucket — the only way staged data leaves
  // storage now that runs never expire.
  const removeRun = async (r) => {
    if (!(await confirm({
      title: `Delete run ${r.runPath}?`,
      body: `This permanently removes the run's whole folder (data/, manifest.json and everything else in it) from ${selected.name}. It cannot be undone.`,
      confirmLabel: 'Delete run',
      variant: 'danger',
    }))) return;
    setDeleting(r.id);
    setNotice(null);
    try {
      await deleteRun(r.id, true);
      const list = await listStagingRuns(selected.id);
      setRuns(Array.isArray(list) ? list : []);
      await load();
      setNotice({ tone: 'success', text: `Deleted ${r.runPath} and its files.` });
    } catch (err) {
      setNotice({ tone: 'danger', text: err?.response?.data?.message || err?.message || 'Delete failed' });
    } finally {
      setDeleting(null);
    }
  };

  // Imports the subsets whose <slug>/subset.json sits under the root and are not on this platform
  // yet (read-only, no sources); re-running refreshes imported runs. Never fails the save it follows.
  const runImport = async (id) => {
    setImporting(true);
    setImportResult({ pending: true });
    try {
      const r = await importStagingSubsets(id);
      setImportResult(r);
      await load();
    } catch (err) {
      setImportResult({ error: err?.response?.data?.message || err?.body?.message || err?.message || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const test = async () => {
    if (!selected) return;
    setTestResult({ pending: true });
    try {
      const r = await testStaging(selected.id);
      setTestResult(r);
      await load();
    } catch (err) {
      setTestResult({ ok: false, message: err?.response?.data?.message || err?.message || 'Test failed' });
    }
  };

  return (
    <FetchContainer fetchingData={loading} absolute={true} error={error}>
      <div className='tdm-page tdm-page--narrow'>
        {confirmDialog}
        <Breadcrumbs items={[{ label: 'Test Data Landscape', to: '/tdm' }, { label: 'Staging' }]} />
        <PageHeader
          icon={BiCloud}
          eyebrow='Test Data Management'
          header='Staging'
          tooltip='Customer-owned storage that holds subset runs'
          meta={<span>Where subset runs are stored. Each run lands in its own dated folder.</span>}
          primaryAction={<Button size='sm' onClick={() => setSelectedId(null)} disabled={!isAdmin} data-testid='tdm-staging-new'>+ New</Button>}
        />
        <div className='tdm-staging-layout'>
          <div>
            <div className='tdm-column__title'><span>Staging · {items.length}</span></div>
            {items.length === 0 && <div className='tdm-empty'>No staging areas yet.</div>}
            {items.map((st) => (
              <button type='button' key={st.id} className={`tdm-list-item${String(st.id) === String(selectedId) ? ' tdm-list-item--on' : ''}`}
                onClick={() => setSelectedId(st.id)} data-testid={`tdm-staging-${st.id}`}>
                <div className='d-flex justify-content-between align-items-center'>
                  <span className='tdm-list-item__name'>{st.name}</span>
                  <span className='tdm-pill'>{st.type.replace('_', ' ')}</span>
                </div>
                <div className='tdm-list-item__sub tdm-mono'>{st.rootPath}</div>
                <div className='tdm-list-item__sub'>{fmtNum(st.subsetCount)} subset{st.subsetCount === 1 ? '' : 's'}{st.lastTestOk != null ? ` · last test ${st.lastTestOk ? 'ok' : 'failed'} ${fmtWhen(st.lastTestAt)}` : ''}
                  {st.dataSourceId != null && <> · <Link to={`/sources/source-editor/${st.dataSourceId}`} onClick={(e) => e.stopPropagation()} data-testid={`tdm-staging-source-${st.id}`}>data source #{st.dataSourceId}</Link></>}</div>
              </button>
            ))}
          </div>

          <div>
            <Form onSubmit={save} className='tdm-section'>
              <div className='tdm-section__title'>{selected ? selected.name : 'New staging'}</div>
              <div className='tdm-section__sub'>The platform stores pointers only, never values. Credentials are encrypted at rest.</div>
              <Row className='text-left mb-2'>
                <Col xs={6}><Form.Group><Form.Label>Name <span className='text-danger'>*</span></Form.Label>
                  <Form.Control size='sm' value={form.name} onChange={(e) => update('name', e.target.value)} required data-testid='tdm-staging-name' /></Form.Group></Col>
                <Col xs={6}><Form.Group><Form.Label>Type <span className='text-danger'>*</span></Form.Label>
                  <Form.Control as='select' size='sm' value={form.type} onChange={(e) => update('type', e.target.value)} data-testid='tdm-staging-type'>
                    {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Form.Control></Form.Group></Col>
              </Row>
              <Row className='text-left mb-2'>
                <Col xs={12}><Form.Group><Form.Label>Root path <span className='text-danger'>*</span></Form.Label>
                  <Form.Control size='sm' placeholder={ROOT_HINT[form.type]} value={form.rootPath} onChange={(e) => update('rootPath', e.target.value)} required data-testid='tdm-staging-root' />
                  <Form.Text className='text-muted'>Layout: {form.rootPath || ROOT_HINT[form.type]}/&lt;subset&gt;/ subset.json · &lt;yyyy-MM-dd-HHmmss&gt;-&lt;run&gt;/ data/ · failed/ · id-maps/ · manifest.json</Form.Text>
                </Form.Group></Col>
              </Row>
              {form.type !== 'SFTP' && (
                <Row className='text-left mb-2'>
                  <Col xs={12}>
                    <Form.Label className='d-block mb-1'>Credentials</Form.Label>
                    <Form.Check inline type='radio' id='cred-inline' name='cred-mode' label='Stored credentials' checked={form.credentialMode === 'INLINE'} onChange={() => update('credentialMode', 'INLINE')} />
                    <Form.Check inline type='radio' id='cred-chain' name='cred-mode' label={form.type === 'S3' ? 'IAM role (default chain)' : 'Managed Identity (default chain)'} checked={form.credentialMode === 'DEFAULT_CHAIN'} onChange={() => update('credentialMode', 'DEFAULT_CHAIN')} />
                  </Col>
                </Row>
              )}
              <StagingCredentialFields type={form.type} credentialMode={form.credentialMode} creds={form.credentials} onChange={updateCred} secretKeys={selected ? selected.secretKeys : []} />
              {notice && <Alert variant={notice.tone} className='py-1 px-2 mb-2'>{notice.text}</Alert>}
              {testResult && (
                <Alert variant={testResult.pending ? 'secondary' : testResult.ok ? 'success' : 'danger'} className='py-1 px-2 mb-2' data-testid='tdm-staging-test-result'>
                  {testResult.pending ? 'Testing…' : testResult.ok
                    ? `CONNECTED · list ${testResult.canList ? '✓' : '✕'} write ${testResult.canWrite ? '✓' : '✕'} read ${testResult.canRead ? '✓' : '✕'} delete ${testResult.canDelete ? '✓' : '✕'}${testResult.latencyMs != null ? ` · ${testResult.latencyMs} ms` : ''}`
                    : `FAILED · ${testResult.message || 'no details'}`}
                </Alert>
              )}
              {importResult && (
                <Alert variant={importResult.pending ? 'secondary' : importResult.error || (importResult.failed || []).length ? 'danger' : 'info'} className='py-1 px-2 mb-2' data-testid='tdm-staging-import-result'>
                  {importResult.pending ? 'Reading subsets already in this storage…' : importResult.error
                    ? `IMPORT FAILED · ${importResult.error}`
                    : <ImportSummary result={importResult} />}
                </Alert>
              )}
              <div className='tdm-actions'>
                <Button size='sm' type='submit' disabled={saving || !isAdmin} data-testid='tdm-staging-save'>{selected ? 'Save staging' : 'Create staging'}</Button>
                <Button size='sm' variant='outline-secondary' type='button' onClick={test} disabled={!selected} data-testid='tdm-staging-test'>Test connection</Button>
                <Button size='sm' variant='outline-secondary' type='button' onClick={() => selected && runImport(selected.id)} disabled={!selected || !isAdmin || importing} data-testid='tdm-staging-import'
                  title='Read every subset.json already under this root and list those subsets here (read-only, not attached to data sources)'>{importing ? 'Importing…' : 'Import subsets'}</Button>
                {selected && <Button size='sm' variant='outline-danger' type='button' onClick={remove} disabled={!isAdmin || selected.subsetCount > 0} title={selected.subsetCount > 0 ? 'Used by subsets' : undefined}>Delete</Button>}
                {!isAdmin && <span className='tdm-note'>Only administrators can change staging credentials.</span>}
              </div>
            </Form>

            {selected && (
              <div className='tdm-section'>
                <div className='tdm-section__title'>Subset runs stored here · {runs.length}</div>
                <div className='tdm-section__sub'>Runs are kept until deleted. Deleting one removes its folder from this storage.</div>
                {runs.length === 0 && <div className='tdm-empty'>No runs yet.</div>}
                {runs.length > 0 && (
                  <div className='tdm-table tdm-table--staging-runs'>
                    <div className='tdm-table__head'><span>Subset / run</span><span>Created</span><span>Size</span><span>Files</span><span>Status</span><span /></div>
                    {runs.map((r) => (
                      <div className='tdm-table__row' key={r.id}>
                        <span className='tdm-mono'>{r.subsetName} /{r.runPath.split('/').slice(1).join('/')}/</span>
                        <span>{fmtWhen(r.startedAt)}</span>
                        <span>{fmtBytes(r.totals && r.totals.bytes)}</span>
                        <span>{fmtNum(r.totals && r.totals.files)}</span>
                        <span className='tdm-pill'>{r.status}</span>
                        <span>
                          <Button size='sm' variant='outline-danger' className='tdm-btn-xs' data-testid={`tdm-run-delete-${r.id}`}
                            disabled={!isAdmin || deleting === r.id || !TERMINAL.includes(r.status)}
                            title={TERMINAL.includes(r.status) ? 'Delete this run and its files' : 'Cancel the run before deleting it'}
                            onClick={() => removeRun(r)}>
                            {deleting === r.id ? 'Deleting…' : 'Delete'}
                          </Button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </FetchContainer>
  );
};

export default StagingManager;
